// **The pinned Beekeeper input bar.**
//
// Lifted verbatim out of `ui/screens/SandboxScreen.tsx`, where it was a private function at the
// bottom of the file, so a second screen can mount the same bar. Props, markup, styles and
// `data-testid`s are unchanged — this is an extraction, not a redesign, and the rounds sandbox
// tests are its regression suite.
//
// Its second mount is the live-mode Colony screen under the **pvp prototype**
// (`docs/dev/pvp-prototype.md`): when the server advertises pvp, the live screen replaces the
// old one-shot `PromptBox` inside the bee-type sheet with this globally pinned bar, exactly as
// rounds mode does, because the pvp Beekeeper is the same multi-turn agent over the whole colony.
// Nothing here knows which screen mounted it.

import { type CSSProperties } from 'react';

import { ChatReentryRow } from '../chat/ChatReentryRow';
import type { ThreadSummary } from '../../chat/transcript-model';
import { submitOnEnter } from '../../keyboard-policy';

/**
 * The field remains full-bleed, but the controls are a working area rather than page chrome. At
 * desktop widths an uncapped transport makes the playhead and its related controls feel unrelated,
 * while an uncapped prompt becomes an unusually long line of text. Below this width these styles
 * resolve to the exact existing mobile geometry (`width: 100%`).
 *
 * Exported so `SandboxScreen`'s own bars measure to the same width from one constant rather than
 * a copy that can drift.
 */
export const PROMPT_BAR_MAX_WIDTH = 960;

/**
 * The Beekeeper prompt's **gated** state (`client-sandbox-screen`, sketch `practice-framing.html`
 * panel C).
 *
 * Present, the prompt input **stays mounted** and states the reason in place, with a control to
 * supply what is missing. The prompt vanishing would misrepresent the design: editing and running
 * are open and only the model is metered, so a stated reason is a door and a missing control is a
 * mystery.
 *
 * It deliberately cannot reach the transport. `mutationGate` closes writes *including* Run; this
 * closes the prompt and nothing else, which is what makes the free/metered split legible.
 */
export interface SandboxPromptLock {
  /** Why prompting is unavailable, in the prompt's own placeholder. */
  reason: string;
  /** The label of the control that supplies what is missing. */
  actionLabel: string;
  /** Opens whatever supplies it. The root owns that surface; this screen owns the affordance. */
  onSupply(): void;
}

export interface PromptBarProps {
  height: number;
  draft: string;
  focused: boolean;
  /** The bee type the message is scoped to, or `null` for the whole colony. */
  scopeName: string | null;
  /** The shared height transition (see `SandboxScreen`'s `growthTransition`), or `undefined` under
   * reduced motion. Threaded rather than recomputed so the bar cannot drift from the world view and
   * the stack it has to move with. */
  growthTransition?: string | undefined;
  /** A chat seam is wired. Absent, Send is not offered at all rather than offered and inert. */
  canSend: boolean;
  /** The rounds mutation gate closes writes but leaves the surrounding watch surface usable. */
  disabled: boolean;
  disabledReason: string;
  /**
   * Prompting is gated for a *missing practice code* rather than by the mutation gate. The bar stays
   * mounted, states the reason in place, and offers the way to supply one — and the transport is
   * untouched, which is the whole point of the free/metered split.
   */
  lock?: SandboxPromptLock | null;
  /**
   * A Beekeeper conversation exists and its overlay is closed, so the bar carries the way back into
   * it (`client-beekeeper-chat`). `null` — no thread, or the overlay is already open — renders no
   * row, and the bar keeps its resting height.
   */
  threadSummary?: ThreadSummary | null;
  /**
   * The channel's own phase flags (`beekeeper-progress-indicator` §2), read only when
   * `threadSummary` is non-null (the reentry row is the only consumer). Computed by the caller from
   * `ChatSeam`'s `phase` rather than from `threadSummary.busy` — that field mirrors
   * `transcript.running` alone, which misses `submitting` (no `turnStarted` observed yet) entirely,
   * and has no way at all to express an ambiguous outcome.
   */
  chatBusy?: boolean;
  chatFailed?: boolean;
  chatUncertain?: boolean;
  /** Narrows the reentry row's `failed` copy for the no-agent-output settle
   * (`beekeeper-progress-indicator` F3) — see `ChatReentryRow`'s own doc comment. Ignored unless
   * `chatFailed` is also set. */
  chatNoOutputSettle?: boolean;
  /**
   * The agent has replied since the player last had the overlay open — the re-entry row's unread
   * halo (`player-chat-and-outcome` **D-3**, `ChatSeamSnapshot.unread`). Read only when
   * `threadSummary` is non-null, like the phase flags above.
   */
  chatUnread?: boolean;
  /**
   * The overlay is **minimised** rather than dismissed (`ChatSeamSnapshot.view`). This is the one
   * place the two non-open views differ — while minimised the row is standing in for a panel the player
   * put away *deliberately*, so it reports the agent's latest line in place of the generic
   * "Beekeeper is working…" (**D-3**). A `closed` thread keeps that copy exactly as
   * `beekeeper-progress-indicator` §2 settled it: nobody is watching a conversation they ended, and
   * the useful statement there is simply that the colony is being worked on.
   */
  chatMinimised?: boolean;
  onReopenThread?(): void;
  onDraft(next: string): void;
  onFocus(): void;
  onBlur(): void;
  onClearScope(): void;
  onSend(): void;
  /** Positioning left to the host screen. `SandboxScreen` supplies none (it is a flex child of the
   * control stack); the pvp Colony screen pins it to the bottom of the frame. */
  style?: CSSProperties;
}

/**
 * Pinned as the stack's third bar rather than nested inside the bee-type sheet: the Beekeeper's
 * unit of work is the **whole colony document**, so a player can say something while watching,
 * without opening a type first.
 *
 * There is no cooldown countdown in this mode (BEEKEEPER §5.1) — the editing phase's clock is the
 * tempo, and a countdown here would be a rule this mode does not have.
 */
export function PromptBar({
  height,
  draft,
  focused,
  scopeName,
  growthTransition,
  canSend,
  disabled,
  disabledReason,
  lock = null,
  threadSummary = null,
  chatBusy = false,
  chatFailed = false,
  chatUncertain = false,
  chatNoOutputSettle = false,
  chatUnread = false,
  chatMinimised = false,
  onReopenThread,
  onDraft,
  onFocus,
  onBlur,
  onClearScope,
  onSend,
  style,
}: PromptBarProps) {
  return (
    <div
      data-testid="sandbox-prompt-bar"
      style={{
        width: '100%',
        maxWidth: PROMPT_BAR_MAX_WIDTH,
        height,
        // Third participant in the same trade: 60px at rest, 86px focused.
        transition: growthTransition,
        boxSizing: 'border-box',
        flex: 'none',
        margin: '0 auto',
        display: 'flex',
        // A column so the re-entry row sits **above** the field rather than beside it — the bar's
        // budget already accounts for the extra 50px (`sandbox-layout.ts`), so nothing floats over
        // the world view to make room.
        flexDirection: 'column',
        justifyContent: 'flex-end',
        gap: 6,
        padding: '8px 12px',
        ...style,
      }}
    >
      {threadSummary !== null && (
        <ChatReentryRow
          lastLine={threadSummary.lastLine}
          // Derived from the same transcript the overlay would be drawing, so a minimised
          // conversation keeps reporting itself here (`player-chat-and-outcome` **D-3**) — and
          // only then; see `chatMinimised`.
          activityLine={chatMinimised ? threadSummary.activityLine : null}
          busy={chatBusy}
          failed={chatFailed}
          uncertain={chatUncertain}
          noOutputSettle={chatNoOutputSettle}
          unread={chatUnread}
          onOpen={() => onReopenThread?.()}
          style={{ flex: 'none' }}
        />
      )}
      <div
        style={{
          display: 'flex',
          alignItems: focused ? 'flex-start' : 'center',
          gap: 8,
        }}
      >
      {/* `focused` drives this field's *growth* only. Its **frame** — resting border, focus ring —
          belongs to the shell, keyed off `data-ui-field="composite"` (`ui/app-shell.css`). The
          wrapper reads as one control but holds a real textarea, so without that marker the player
          sees two rings: this frame's and the shell's generic one on the textarea inside it. The
          border is deliberately not declared here either; inline would outrank the shell's focused
          accent edge and quietly reduce the focused state to a glow. One owner for the frame. */}
      <div
        data-ui-field="composite"
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: focused ? 'flex-start' : 'center',
          gap: 6,
          height: focused ? 70 : 44,
          // The field's own growth, on the same clock as the three above it.
          transition: growthTransition,
          boxSizing: 'border-box',
          padding: focused ? '7px 8px' : '0 6px',
          background: 'var(--surface-card)',
        }}
      >
        {scopeName === null ? (
          <span aria-hidden="true" style={{ flex: 'none', fontSize: 14, paddingLeft: 2 }}>
            🐝
          </span>
        ) : (
          // The scope pill REPLACES the 🐝 as the field's leading element, so scoping costs no
          // extra row (visual spec §5.3).
          <span
            data-testid="sandbox-prompt-scope"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              flex: 'none',
              height: 30,
              padding: '0 4px 0 8px',
              borderRadius: 'var(--r-pill)',
              background: 'var(--accent-soft)',
              border: '1px solid var(--honey-300)',
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              // On the honey tint, not on cream (UI/UX audit F-06): --accent-strong measures 4.49:1
              // here, --accent-on-soft 5.48:1.
              color: 'var(--accent-on-soft)',
            }}
          >
            {/* Capped and ellipsised (UI/UX audit F-27): a long bee-type name must not take the
                field's room. `minWidth: 0` is what allows the ellipsis inside a flex row. */}
            <span
              style={{
                maxWidth: 84,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={scopeName}
            >
              {scopeName}
            </span>
            <button
              type="button"
              data-testid="sandbox-prompt-scope-clear"
              aria-label={`Clear the ${scopeName} scope`}
              onClick={onClearScope}
              style={{
                // 22 × 22, the exemption visual spec §0.3 grants — see the file header.
                width: 22,
                height: 22,
                border: 'none',
                background: 'transparent',
                color: 'var(--accent-on-soft)',
                fontSize: 11,
                borderRadius: 11,
                cursor: 'pointer',
              }}
            >
              ✕
            </button>
          </span>
        )}
        <textarea
          data-testid="sandbox-prompt-input"
          // Keeps the shell's standalone-control padding and min-height off this one: the frame
          // around it owns both, and the user-agent metrics the block-axis note below reasons
          // about stay exactly as they were (`ui/app-shell.css`).
          data-ui-field-control=""
          value={draft}
          rows={focused ? 3 : 1}
          // **UI/UX audit F-27, and a correction to it.** The reason truncates mid-word at 375px
          // ("Sandbox is closed for this"), and F-27's proposed fix was to shorten it here on the
          // grounds that the reason is stated elsewhere anyway. That is true for the **mutation
          // gate** — `sandbox-mutation-gate-reason` renders it above the bar — but **not** for the
          // practice-code lock, whose reason has no other visible home. Shortening it there would
          // have removed the only statement of why the field is inert, which is a worse defect than
          // the truncation. So the placeholder keeps the full reason, and F-27 is addressed by the
          // levers that give the field room instead: the scope chip is now capped and ellipsised
          // (below), and `minWidth: 0` stays, since that is what lets this flex item shrink at all.
          //
          // The residual — the longest reasons still clip at 375px — wants a wrapping notice line in
          // the bar, which needs the bar's fixed height to become intrinsic first. Recorded rather
          // than papered over.
          placeholder={
            disabled
              ? disabledReason
              : scopeName === null
                ? 'Tell the Beekeeper what to change…'
                : 'Tell the Beekeeper…'
          }
          // The full text is reachable on hover even where the field clips it. Deliberately a
          // `title`, never an `aria-label`: F-22 is that exact mistake made on the Run button, where
          // the gate reason replaced "Run this version" and left the control with no name.
          title={disabled ? disabledReason : undefined}
          disabled={disabled}
          onChange={(event) => onDraft(event.target.value)}
          // **UI/UX audit F-02.** Enter sends, Shift+Enter inserts a newline. The guard repeats the
          // Send button's own render and disabled conditions (`canSend && lock === null`, then
          // `!disabled && draft.trim() !== ''`) rather than calling `onSend` unconditionally, so the
          // keyboard can never reach a send the button itself would refuse — including while a lock
          // is up, where the offered action is "supply a code", not "send".
          onKeyDown={submitOnEnter(() => {
            if (canSend && lock === null && !disabled && draft.trim() !== '') {
              onSend();
              return true;
            }
            // Declined, so the Enter is *not* consumed (review follow-up): with a practice lock up
            // this textarea is enabled, and swallowing the key there left Enter doing nothing at all.
            return false;
          })}
          onFocus={onFocus}
          onBlur={onBlur}
          style={{
            flex: 1,
            minWidth: 0,
            // **Fills the frame's content box when grown, rather than taking its intrinsic height.**
            // `rows={3}` at this font is ~60.5px tall, but the focused frame's content box is 54.4px;
            // with the row's `alignItems: 'flex-start'` the excess spilled downward and ate the
            // frame's 7px bottom padding, leaving 7.8px above the field and 1.65px below it.
            //
            // **Focus-dependent, never unconditional.** At rest the frame is 44px with
            // `alignItems: 'center'`, and stretching there would fill it — and because a textarea's
            // own text is top-aligned, the single resting line would jump ~9.8px upward. That
            // regression has its own test beside the containment one.
            alignSelf: focused ? 'stretch' : undefined,
            border: 'none',
            outline: 'none',
            resize: 'none',
            background: 'transparent',
            // **Block axis only, and only while focused.** Once the field is clamped to the frame's
            // content box, the user agent's default 2px vertical padding comes out of the *draft's*
            // room rather than sitting outside it, cutting the visible text by about a fifth of a
            // line. Zeroing it recovers that.
            //
            // Deliberately not a plain `padding: 0`, which was the first attempt: that also strips
            // the *inline* padding and applies at rest, where nothing is clamped and there is nothing
            // to recover — it merely shifted the resting text and caret 2px toward the edges and cut
            // the resting field from 22.85px to 18.85px, in a state this change is supposed to leave
            // alone.
            paddingBlock: focused ? 0 : undefined,
            // Wraps, and scrolls past the third line rather than growing without bound.
            overflowY: 'auto',
            fontFamily: 'var(--font-body)',
            fontSize: 13,
            lineHeight: 1.45,
            color: 'var(--ink-1)',
          }}
        />
      </div>
      {/* **The reason, in place.** Rendered beside the field rather than replacing it: the input
          stays mounted (its placeholder carries the reason too), and the control that supplies the
          missing code is offered right where the player just tried to use it. */}
      {lock !== null && (
        <button
          type="button"
          data-testid="sandbox-prompt-unlock"
          // Same reason as the Send button below: this bar re-lays-out when the prompt loses focus,
          // so a press that blurs it moves this button out from under the release.
          onMouseDown={(event) => event.preventDefault()}
          onClick={lock.onSupply}
          style={{
            flex: 'none',
            height: 'var(--hit-target)',
            minWidth: 'var(--hit-target)',
            alignSelf: focused ? 'flex-start' : undefined,
            padding: '0 14px',
            borderRadius: 'var(--r-pill)',
            background: 'var(--surface-card)',
            border: '1.5px solid var(--line-strong)',
            color: 'var(--ink-1)',
            fontFamily: 'var(--font-display)',
            fontWeight: 500,
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          {lock.actionLabel}
        </button>
      )}
      {canSend && lock === null && (
        <button
          type="button"
          data-testid="sandbox-prompt-send"
          disabled={disabled || draft.trim() === ''}
          // **The press must not blur the field**, or the click never happens at all.
          //
          // This bar lays out differently while the prompt has focus (`alignSelf` above, and the
          // taller `focused` prompt height the stack allocates). A plain press therefore ran:
          // mousedown → the textarea blurs → `focused` flips → the bar re-lays-out and this button
          // moves ~26px → mouseup lands where the button no longer is. A `click` event only fires
          // when press and release land on the *same* element, so it never fired: a player who
          // typed a prompt and clicked Send got silence, and had to click a second time.
          //
          // Preventing the default on mousedown keeps focus in the textarea, so nothing moves
          // between press and release. The alternative — making the bar's geometry independent of
          // focus — is a visual-layer change this line deliberately does not make.
          onMouseDown={(event) => event.preventDefault()}
          // Submittable with no type open: the whole colony document is the unit of work.
          onClick={onSend}
          style={{
            flex: 'none',
            height: 'var(--hit-target)',
            minWidth: 'var(--hit-target)',
            alignSelf: focused ? 'flex-start' : undefined,
            padding: '0 18px',
            borderRadius: 'var(--r-pill)',
            background: 'var(--accent-fill)',
            border: '1px solid var(--accent-strong)',
            color: '#FFFDF6',
            fontFamily: 'var(--font-display)',
            fontWeight: 500,
            fontSize: 14,
            opacity: disabled || draft.trim() === '' ? 0.38 : 1,
            cursor: disabled || draft.trim() === '' ? 'default' : 'pointer',
          }}
        >
          Send
        </button>
      )}
      </div>
    </div>
  );
}
